// =============================================================
// libraryImport.js — turns a dropped folder tree into company-library records.
//
// Input: Array<{ file, relativePath }> from docFolders' collect helpers, where
// relativePath is the folder the file sat in ('' at the top of the drop).
//
// The layout this reads, from a real tree:
//
//   <picked folder>/            optional container (stripped when it holds projects)
//     FDEM/                     ← one PROJECT = one draft past-contract record
//       NEW RFP/…, OLD RFP/…    ← sub-folders say what the files are, nothing more
//       Final 284…xlsx          ← files directly in the project belong to it too
//     Capability Statements/    ← a MATERIAL folder: one material record per file
//     Smartsheet…Invoice.pdf    ← a loose file belongs to no project: left out, listed
//
// Pure: no DOM, no network. The server decides each file's type from its name
// and folder (folderPath travels with every upload).
// =============================================================

export const MAX_FILE_BYTES = 100 * 1024 * 1024;

const JUNK = /^(\.ds_store|thumbs\.db|desktop\.ini|icon\r?)$/i;
const LOCK = /^~\$/;
const MATERIAL_FOLDER = /\b(capabilit(y|ies)|cap statements?|resumes?|résumés?|key personnel|staff bios?|boilerplate|company (material|info|profile)|marketing|templates?)\b/i;

const segs = (p) => String(p || '').split('/').map(s => s.trim()).filter(Boolean);
const stripExt = (n) => String(n || '').replace(/\.[A-Za-z0-9]{1,6}$/, '');

// Folder names that describe FILES ("NEW RFP", "Awards", "Agreement") rather
// than a project. When most proposed records are named like this, the person
// probably picked one project's folder with the container box ticked.
const FILE_KIND_FOLDER = /\b(rfps?|rfqs?|ifbs?|solicitations?|awards?|contracts?|proposals?|agreements?|amendments?|mods?|modifications?|invoices?|reports?|pricing|attachments?|old|new)\b/i;
export function looksLikeSubfolders(records) {
  const named = (records || []).filter(r => r.kind === 'pastContract');
  if (named.length < 2) return false;
  const hits = named.filter(r => FILE_KIND_FOLDER.test(String(r.title).replace(/[_\-.]+/g, ' '))).length;
  return hits * 2 >= named.length;
}

export function isMaterialFolder(name) {
  return MATERIAL_FOLDER.test(String(name || '').replace(/[_\-.]+/g, ' '));
}

// Should the first folder be dropped as a container? Yes when every file sits
// under the SAME first folder and that folder holds at least one sub-folder —
// i.e. the person picked the folder that holds the projects. A single project
// folder dropped on its own (only files, or files plus RFP sub-folders) is
// ambiguous; `containerMode` lets the screen override the guess.
export function guessContainer(entries) {
  const firsts = new Set();
  let hasSub = false;
  for (const e of entries) {
    const p = segs(e.relativePath);
    if (!p.length) return false; // a file at the very top: no single container
    firsts.add(p[0]);
    if (p.length >= 2) hasSub = true;
  }
  return firsts.size === 1 && hasSub;
}

/**
 * Build the import plan.
 * @param entries Array<{ file, relativePath }>
 * @param opts.container true = the top folder holds the projects; false = each
 *        top folder is a project; undefined = guess.
 * @returns {{ container, records, loose, skipped, fileCount, bytes }}
 *   records: [{ key, kind: 'pastContract'|'material', title, folder, files: [{ file, folderPath }] }]
 */
export function planLibraryImport(entries, { container } = {}) {
  const list = Array.isArray(entries) ? entries : [];
  const useContainer = container === undefined ? guessContainer(list) : !!container;
  const records = new Map();
  const loose = [];
  const skipped = [];
  let fileCount = 0;
  let bytes = 0;

  for (const e of list) {
    const file = e && e.file;
    if (!file) continue;
    const name = String(file.name || '');
    if (JUNK.test(name) || LOCK.test(name) || name.startsWith('.')) continue;
    if (!file.size) { skipped.push({ name, reason: 'empty file' }); continue; }
    if (file.size > MAX_FILE_BYTES) { skipped.push({ name, reason: `over ${MAX_FILE_BYTES / 1048576} MB` }); continue; }

    let p = segs(e.relativePath);
    if (useContainer) p = p.slice(1);
    if (!p.length) { loose.push({ file, name }); continue; }
    const project = p[0];
    const inner = p.slice(1).join('/');

    if (isMaterialFolder(project)) {
      const title = stripExt(name) || name;
      const key = `material:${project}/${inner}/${name}`.toLowerCase();
      records.set(key, { key, kind: 'material', title, folder: project, files: [{ file, folderPath: inner }] });
    } else {
      const key = `project:${project}`.toLowerCase();
      if (!records.has(key)) records.set(key, { key, kind: 'pastContract', title: project, folder: project, files: [] });
      records.get(key).files.push({ file, folderPath: inner });
    }
    fileCount += 1;
    bytes += file.size;
  }

  const ordered = [...records.values()].sort((a, b) =>
    (a.kind === b.kind ? 0 : a.kind === 'pastContract' ? -1 : 1) || a.title.localeCompare(b.title));
  return { container: useContainer, records: ordered, loose, skipped, fileCount, bytes };
}

// The upload order: record by record, so a record's files arrive together and
// it can be queued for the automatic read as soon as its last file lands.
export function uploadQueue(records, existingFilesByItem = {}) {
  const out = [];
  for (const r of records) {
    const have = existingFilesByItem[r.itemId] || [];
    const seen = new Set(have.map(f => `${String(f.fileName).toLowerCase()}|${Number(f.sizeBytes) || 0}`));
    for (const f of r.files) {
      const k = `${String(f.file.name).toLowerCase()}|${Number(f.file.size) || 0}`;
      if (seen.has(k)) continue; // already on this record from an earlier import
      seen.add(k);
      out.push({ itemId: r.itemId, key: r.key, file: f.file, folderPath: f.folderPath });
    }
  }
  return out;
}

// Run `fn` over `items` with at most `limit` in flight. Resolves to the results
// in order; one failure never stops the rest.
export async function runPool(items, limit, fn) {
  const results = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      try { results[i] = { ok: true, value: await fn(items[i], i) }; }
      catch (err) { results[i] = { ok: false, error: err }; }
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, worker));
  return results;
}
